import { useState, useCallback, useEffect } from 'react';
import { Clock, ArrowRightLeft, Copy, Check, RefreshCw } from 'lucide-react';

// In production, nginx serves both frontend and API from same origin
export function EpochConverter() {
  const [epochInput, setEpochInput] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [currentEpoch, setCurrentEpoch] = useState(Math.floor(Date.now() / 1000));
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [epochResult, setEpochResult] = useState<{
    seconds: number;
    milliseconds: number;
    iso: string;
    utc: string;
    local: string;
    relative: string;
    browserTimeAtConversion: string;
  } | null>(null);
  const [dateResult, setDateResult] = useState<{
    seconds: number;
    milliseconds: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerDateTime, setPickerDateTime] = useState('');

  // Update current epoch every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentEpoch(Math.floor(Date.now() / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const getRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffSecs = Math.trunc(diffMs / 1000);
    const diffMins = Math.trunc(diffSecs / 60);
    const diffHours = Math.trunc(diffMins / 60);
    const diffDays = Math.trunc(diffHours / 24);
    const diffYears = Math.trunc(diffDays / 365);

    if (diffYears !== 0) {
      return diffYears > 0 ? `${diffYears} year${diffYears !== 1 ? 's' : ''} from now` : `${Math.abs(diffYears)} year${Math.abs(diffYears) !== 1 ? 's' : ''} ago`;
    }
    if (diffDays !== 0) {
      return diffDays > 0 ? `${diffDays} day${diffDays !== 1 ? 's' : ''} from now` : `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
    }
    if (diffHours !== 0) {
      return diffHours > 0 ? `${diffHours} hour${diffHours !== 1 ? 's' : ''} from now` : `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ago`;
    }
    if (diffMins !== 0) {
      return diffMins > 0 ? `${diffMins} minute${diffMins !== 1 ? 's' : ''} from now` : `${Math.abs(diffMins)} minute${Math.abs(diffMins) !== 1 ? 's' : ''} ago`;
    }
    if (diffSecs !== 0) {
      return diffSecs > 0 ? `${diffSecs} second${diffSecs !== 1 ? 's' : ''} from now` : `${Math.abs(diffSecs)} second${Math.abs(diffSecs) !== 1 ? 's' : ''} ago`;
    }
    return 'now';
  };

  const handleEpochConvert = useCallback(() => {
    setError(null);
    setEpochResult(null);

    const input = epochInput.trim();
    if (!input) {
      setError('Please enter an epoch timestamp');
      return;
    }

    let timestamp = parseInt(input, 10);
    if (isNaN(timestamp)) {
      setError('Invalid epoch timestamp');
      return;
    }

    // Detect if it's milliseconds (13+ digits) or seconds (10 digits)
    let isMilliseconds = input.length >= 13;
    let timestampMs = isMilliseconds ? timestamp : timestamp * 1000;

    // Validate reasonable date range (year 1970 to 3000)
    if (timestampMs < 0 || timestampMs > 32503680000000) {
      setError('Timestamp out of valid range');
      return;
    }

    const date = new Date(timestampMs);
    const now = new Date();

    setEpochResult({
      seconds: Math.floor(timestampMs / 1000),
      milliseconds: timestampMs,
      iso: date.toISOString(),
      utc: date.toUTCString(),
      local: date.toLocaleString(),
      relative: getRelativeTime(date),
      browserTimeAtConversion: now.toISOString(),
    });
  }, [epochInput]);

  const handleDateConvert = useCallback(() => {
    setError(null);
    setDateResult(null);

    const input = dateInput.trim();
    if (!input) {
      setError('Please enter a date');
      return;
    }

    const date = new Date(input);
    if (isNaN(date.getTime())) {
      setError('Invalid date format. Try: YYYY-MM-DD, YYYY-MM-DD HH:mm:ss, or ISO 8601');
      return;
    }

    setDateResult({
      seconds: Math.floor(date.getTime() / 1000),
      milliseconds: date.getTime(),
    });
  }, [dateInput]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleUseCurrentTime = () => {
    setEpochInput(currentEpoch.toString());
  };

  const handleUseCurrentDate = () => {
    const now = new Date();
    // Format for datetime-local: YYYY-MM-DDTHH:mm:ss in LOCAL time
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const formatted = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    setPickerDateTime(formatted);
    setDateInput(formatted);
  };

  const handlePickerChange = (value: string) => {
    setPickerDateTime(value);
    if (value) {
      setDateInput(value);
    }
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <button
      onClick={() => handleCopy(text, field)}
      className="ml-2 p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
      title="Copy"
    >
      {copiedField === field ? (
        <Check className="w-3 h-3 text-[var(--accent)]" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Current Time Display */}
      <div className="flex items-center justify-center gap-4 p-4 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)]">
        <Clock className="w-5 h-5 text-[var(--accent)]" />
        <div className="text-center">
          <div className="text-sm text-[var(--text-muted)]">Current Unix Timestamp</div>
          <div className="font-mono text-2xl text-[var(--accent)]">{currentEpoch}</div>
        </div>
        <button
          onClick={() => handleCopy(currentEpoch.toString(), 'current')}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
          title="Copy current timestamp"
        >
          {copiedField === 'current' ? (
            <Check className="w-4 h-4 text-[var(--accent)]" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Epoch to Date */}
        <div className="space-y-4">
          <h3 className="text-[var(--accent)] font-semibold flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Epoch → Date
          </h3>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Unix Timestamp
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono"
                placeholder="1709049600 or 1709049600000"
                value={epochInput}
                onChange={(e) => setEpochInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEpochConvert()}
              />
              <button
                onClick={handleUseCurrentTime}
                className="px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--accent-dim)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
                title="Use current time"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <button
            onClick={handleEpochConvert}
            disabled={!epochInput.trim()}
            className="btn-primary flex items-center gap-2 w-full justify-center"
          >
            <Clock className="w-4 h-4" />
            Convert to Date
          </button>

          {epochResult && (
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)] space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">ISO 8601:</span>
                <span className="font-mono flex items-center">
                  {epochResult.iso}
                  <CopyButton text={epochResult.iso} field="iso" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">UTC:</span>
                <span className="font-mono flex items-center">
                  {epochResult.utc}
                  <CopyButton text={epochResult.utc} field="utc" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Local:</span>
                <span className="font-mono flex items-center">
                  {epochResult.local}
                  <CopyButton text={epochResult.local} field="local" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Relative:</span>
                <span className="text-[var(--accent)]">{epochResult.relative}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-xs pt-1">
                <span className="text-[var(--text-muted)]">Current time (at conversion):</span>
                <span className="text-[var(--text-muted)] font-mono">{epochResult.browserTimeAtConversion}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-[var(--border-color)]">
                <span className="text-[var(--text-muted)]">Seconds:</span>
                <span className="font-mono flex items-center">
                  {epochResult.seconds}
                  <CopyButton text={epochResult.seconds.toString()} field="epoch-sec" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Milliseconds:</span>
                <span className="font-mono flex items-center">
                  {epochResult.milliseconds}
                  <CopyButton text={epochResult.milliseconds.toString()} field="epoch-ms" />
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Date to Epoch */}
        <div className="space-y-4">
          <h3 className="text-[var(--accent)] font-semibold flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Date → Epoch
          </h3>
          
          <div className="space-y-3">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Date/Time
            </label>
            
            {/* DateTime Picker */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-[var(--text-muted)] mb-1">Select Date & Time</label>
                <input
                  type="datetime-local"
                  step="1"
                  className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent [color-scheme:dark]"
                  value={pickerDateTime}
                  onChange={(e) => handlePickerChange(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleUseCurrentDate}
                  className="px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--accent-dim)] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
                  title="Use current date/time"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Manual input */}
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Or enter manually (ISO 8601)</label>
              <input
                type="text"
                className="w-full px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
                placeholder="2024-02-27T12:00:00 or 2024-02-27"
                value={dateInput}
                onChange={(e) => setDateInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleDateConvert()}
              />
            </div>
          </div>

          <button
            onClick={handleDateConvert}
            disabled={!dateInput.trim()}
            className="btn-primary flex items-center gap-2 w-full justify-center"
          >
            <Clock className="w-4 h-4" />
            Convert to Epoch
          </button>

          {dateResult && (
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg border border-[var(--border-color)] space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Unix Timestamp (seconds):</span>
                <span className="font-mono text-lg text-[var(--accent)] flex items-center">
                  {dateResult.seconds}
                  <CopyButton text={dateResult.seconds.toString()} field="date-sec" />
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--text-muted)]">Unix Timestamp (ms):</span>
                <span className="font-mono flex items-center">
                  {dateResult.milliseconds}
                  <CopyButton text={dateResult.milliseconds.toString()} field="date-ms" />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Tip:</span> Epoch timestamps can be in seconds (10 digits) or milliseconds (13 digits). Both formats are automatically detected.
      </div>
    </div>
  );
}
